'use client';
import { createTheme, type ThemeOptions } from '@mui/material/styles';
import siteConfig from '@/config/site';

/**
 * Theme derived from config/site brand colours. The palette is tuned here into
 * tinted neutrals (warm cream surfaces, espresso text) so the site reads as a
 * crafted atelier rather than flat white bands. Headings use Fraunces (a soft
 * serif with character), body uses Inter — the two families are loaded as CSS
 * variables in app/layout.tsx.
 */
const base: ThemeOptions = {
  palette: {
    mode: siteConfig.brand.mode,
    primary: { main: siteConfig.brand.primary, contrastText: '#fbf6ef' },
    secondary: { main: siteConfig.brand.secondary, contrastText: '#ffffff' },
    background: {
      default: '#fbf6ef',
      paper: '#fffbf4',
    },
    text: {
      primary: '#2a1e16',
      secondary: '#6b5a4c',
    },
    divider: 'rgba(58, 42, 31, 0.12)',
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: 'var(--font-inter), system-ui, -apple-system, sans-serif',
    fontSize: 16,
    h1: { fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1 },
    h2: { fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.15 },
    h3: { fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 600, lineHeight: 1.2 },
    h4: { fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 600, lineHeight: 1.25 },
    h5: { fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 600, lineHeight: 1.3 },
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.65 },
    button: { textTransform: 'none', fontWeight: 600 },
    overline: { letterSpacing: '0.14em', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 22, paddingBlock: 9 },
        sizeLarge: { paddingInline: 30, paddingBlock: 12, fontSize: '1.02rem' },
      },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 18 } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
    },
  },
};

export const theme = createTheme(base);
export default theme;
